<template>
    <div class="artshare-container">
        <div class="title">NEW &nbsp;&nbsp;PLATFORM &nbsp;&nbsp;FOR &nbsp;&nbsp;ART&nbsp;&nbsp;DISSEMINATION</div>
        <div class="desc">艺术传播新平台</div>
        <div class="art-type">
            <div class="art-type-item">保障艺术家权益</div>
            <div class="art-type-item">守护艺术品价值</div>
            <div class="art-type-item">重塑艺术界生态</div>
        </div>

        <!-- 轮播容器 -->
        <div class="carousel-simple-container">

            <div class="carousel-track" ref="track">
                <!-- 添加transitionend事件监听 -->
                <div class="carousel-slide" :style="carouselStyle" @transitionend="handleTransitionEnd">
                    <div class="carousel-item" v-for="(item, index) in displayArtList" :key="index + item.id">
                        <img :src="item.imageUrl" :alt="item.name" class="art-image">
                        <div class="desc-box">
                            <div class="title">{{ item.name }}</div>
                            <div class="name">{{ item.createUser }}</div>
                            <div class="time">上传时间: {{ formatDate(item.updateTime) }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="carousel-dots" v-if="artList.length > 4">
                <span v-for="i in totalSlides" :key="i" :class="{ active: currentSlide === i - 1 }"
                    @click="goToSlide(i - 1)"></span>
            </div>
        </div>
    </div>
</template>

<script>
import { getArtshare } from '@/api/getArtshare'

export default {
    name: 'Artshare',
    data () {
        return {
            artList: [],
            currentIndex: 0, // 当前滚动位置索引
            itemsPerPage: 4, // 每页显示4个项目
            autoPlayTimer: null,
            isTransitioning: false // 过渡状态标记
        }
    },
    computed: {
        // 计算总幻灯片数量（基于原始数据）
        totalSlides () {
            return Math.ceil(this.artList.length / this.itemsPerPage)
        },
        // 当前显示的幻灯片索引（用于指示器）
        currentSlide () {
            return Math.floor((this.currentIndex - this.itemsPerPage) % this.totalSlides)
        },
        // 处理显示列表（添加无缝切换的首尾复制）
        displayArtList () {
            if (this.artList.length <= this.itemsPerPage) return this.artList
            // 复制前4个和后4个项目用于无缝切换
            const firstPart = this.artList.slice(0, this.itemsPerPage)
            const lastPart = this.artList.slice(-this.itemsPerPage)
            return [...lastPart, ...this.artList, ...firstPart]
        },
        // 计算当前滚动样式
        carouselStyle () {
            return {
                transform: `translateX(-${this.currentIndex * (100 / this.itemsPerPage)}%)`,
                transition: this.isTransitioning ? 'transform 0.5s ease-in-out' : 'none'
            }
        }
    },
    async created () {
        try {
            const data = await getArtshare()
            this.artList = data
            // console.log('获取到的艺术作品数量:', this.artList.length)

            // 确保至少有8个项目用于轮播
            if (this.artList.length < 8) {
                this.artList = [...this.artList, ...this.generateMockData(8 - this.artList.length)]
            }

            // 初始设置currentIndex为itemsPerPage，显示原始数据的第一页
            this.currentIndex = this.itemsPerPage
            this.startAutoPlay()
        } catch (error) {
            console.error('加载艺术作品失败:', error)
            this.artList = this.generateMockData(8)
            this.currentIndex = this.itemsPerPage
            this.startAutoPlay()
        }
    },
    mounted () {
        window.addEventListener('resize', this.handleResize)
        // 确保DOM渲染完成后启动自动播放
        this.$nextTick(() => {
            this.startAutoPlay()
        })
    },
    beforeUnmount () {
        this.stopAutoPlay()
        window.removeEventListener('resize', this.handleResize)
    },
    methods: {
        // 生成模拟数据
        generateMockData (count) {
            return Array.from({ length: count }, (_, i) => ({
                id: `mock-${Date.now()}-${i}`,
                imageUrl: `https://picsum.photos/seed/${Date.now()}-${i}/400/500`,
                name: `艺术作品 ${String.fromCharCode(65 + i)}`,
                createUser: `艺术家 ${i + 1}`,
                updateTime: new Date(Date.now() - i * 24 * 60 * 60 * 1000).toISOString()
            }))
        },

        formatDate (dateString) {
            if (!dateString) return ''
            return new Date(dateString).toLocaleDateString('zh-CN')
        },

        startAutoPlay () {
            // 先清除可能存在的定时器
            this.stopAutoPlay()
            // 只有当有足够项目时才启动自动播放
            if (this.artList.length > this.itemsPerPage) {
                this.autoPlayTimer = setInterval(() => {
                    this.nextSlide()
                }, 5000)
                // console.log('自动轮播已启动')
            }
        },

        stopAutoPlay () {
            if (this.autoPlayTimer) {
                clearInterval(this.autoPlayTimer)
                this.autoPlayTimer = null
                // console.log('自动轮播已停止')
            }
        },

        // 下一页
        nextSlide () {
            if (this.isTransitioning || this.artList.length <= this.itemsPerPage) return
            this.isTransitioning = true
            this.currentIndex++
            // 重置自动播放定时器
            this.startAutoPlay()
        },

        // 上一页
        prevSlide () {
            if (this.isTransitioning || this.artList.length <= this.itemsPerPage) return
            this.isTransitioning = true
            this.currentIndex--
            // 重置自动播放定时器
            this.startAutoPlay()
        },

        // 跳转到指定幻灯片
        goToSlide (index) {
            if (this.isTransitioning) return
            this.isTransitioning = true
            // 转换为实际的索引位置（加上前面复制的部分）
            this.currentIndex = index + this.itemsPerPage
            // 重置自动播放定时器
            this.startAutoPlay()
        },

        // 处理过渡结束事件
        handleTransitionEnd () {
            this.isTransitioning = false
            const totalItems = this.displayArtList.length
            const middleStart = this.itemsPerPage
            const middleEnd = totalItems - this.itemsPerPage

            // 无缝切换逻辑：当滚动到复制的尾部时，跳转到原始数据的头部
            if (this.currentIndex >= middleEnd) {
                this.currentIndex = middleStart + (this.currentIndex - middleEnd)
            }
            // 当滚动到复制的头部时，跳转到原始数据的尾部
            else if (this.currentIndex < middleStart) {
                this.currentIndex = middleEnd + (this.currentIndex - middleStart)
            }
        },

        handleResize () {
            // 窗口大小变化时重置
            this.currentIndex = this.itemsPerPage
        }
    }
}
</script>

<style lang="less">
.artshare-container {
    padding: 100px 20px 50px;
    width: 100%;
    box-sizing: border-box;
    text-align: center;

    .title {
        font-size: 16px;
        font-weight: 900;
        color: #B0AEAF;
        letter-spacing: 2px;
    }

    .desc {
        margin-top: 20px;
        font-size: 32px;
        font-weight: 900;
        color: #FF793A;
    }

    .art-type {
        display: flex;
        justify-content: space-around;
        gap: 20px;
        margin: 40px 0;
        flex-wrap: wrap;

        .art-type-item {
            min-width: 220px;
            height: 50px;
            font-size: 24px;
            font-weight: 900;
            background-color: #FF793A;
            color: white;
            border-radius: 24px;
            line-height: 50px;
            padding: 0 20px;
        }
    }

    .carousel-simple-container {
        position: relative;
        width: 100%;
        margin: 0 auto;

        .carousel-control {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            width: 50px;
            height: 50px;
            background-color: rgba(255, 255, 255, 0.8);
            border: none;
            border-radius: 50%;
            font-size: 24px;
            color: #FF793A;
            cursor: pointer;
            z-index: 10;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;

            &:hover {
                background-color: white;
                transform: translateY(-50%) scale(1.1);
            }

            &.prev {
                left: 10px;
            }

            &.next {
                right: 10px;
            }
        }

        .carousel-track {
            width: 100%;
            overflow: hidden;
            position: relative;

            .carousel-slide {
                display: flex;
                width: 100%;
                transition: transform 0.5s ease-in-out;

                .carousel-item {
                    width: 25%;
                    padding: 0 10px;
                    box-sizing: border-box;
                    flex-shrink: 0;

                    .art-image {
                        width: 100%;
                        height: 350px;
                        object-fit: cover;
                        border-radius: 8px;
                    }

                    .desc-box {
                        width: 100%;
                        padding: 15px 10px;
                        text-align: left;
                        box-sizing: border-box;

                        .title {
                            font-size: 20px;
                            font-weight: 900;
                            color: #000;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }

                        .name {
                            font-size: 16px;
                            color: #ACACAC;
                            line-height: 30px;
                        }

                        .time {
                            font-size: 14px;
                            color: #ACACAC;
                        }
                    }
                }
            }
        }

        .carousel-dots {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 30px;
            box-sizing: border-box;

            span {
                width: 12px;
                height: 12px;
                border-radius: 50%;
                background-color: #ddd;
                cursor: pointer;
                transition: all 0.3s ease;

                &.active {
                    background-color: #FF793A;
                    transform: scale(1.3);
                }
            }
        }
    }
}

/* 确保在所有屏幕尺寸下都显示4个项目 */
@media (max-width: 1200px),
(max-width: 992px),
(max-width: 768px),
(max-width: 576px) {
    .carousel-item {
        width: 25% !important;
        flex-shrink: 0 !important;
    }
}
</style>